import React from "react";
import { Collapse, MenuItem, Select } from "@mui/material";
import { FormattedMessage } from "react-intl";
import { Controller } from "react-hook-form";
import DropDownArrowIcon from "../../assets/icons/DropDownArrowIcon";
import BaseFormField from "./BaseFormField";

/**
 * Form field frame — the `.onbarding-input` look shared with BaseTextField.
 * Colors are colors_v2 CSS custom properties so the field stays theme-driven.
 */
const FIELD = {
  height: 40,
  radius: "6px",
  bg: "var(--input-background-default)",
  border: "var(--new-border)",
  // Hover/focus borders match .onbarding-input exactly (BaseTextField): hover
  // steps to --new-border-accent, focus to --font-heavy-default with no ring.
  borderHover: "var(--new-border-accent)",
  borderFocus: "var(--font-heavy-default)",
  text: "var(--input-content-filled)",
  placeholder: "var(--input-content-empty)",
  errorBorder: "var(--error-error-80)",
  disabledBorder: "var(--new-muted-border)",
  disabledBg: "var(--app-body)",
  disabledText: "var(--new-foreground)",
  disabledIcon: "var(--new-disabled)"
};

/**
 * BaseDropdown — the canonical reskin select (promoted from FormDropDownReskin,
 * behavior unchanged).
 *
 * WHAT IT IS: a labelled select whose frame matches the `.onbarding-input` text
 * fields beside it (40px tall, --input-background-default fill, 1px --new-border,
 * 6px radius), wired to react-hook-form via `Controller`.
 *
 * WHAT IT WRAPS: a MUI standard-variant `Select` (the control) inside
 * `BaseFormField` (label + error scaffold).
 *
 * CONTRACT (props in):
 *  - label: i18n id; doubles as the field `name`, `id`, and Controller key.
 *  - control, errors: react-hook-form wiring.
 *  - required, disabled: forwarded to the Select / FormControl.
 *  - placeholder: node shown as the empty/displayEmpty option.
 *  - hidePlaceholder: omit the empty placeholder row entirely (for fields that
 *      always hold a valid value, e.g. language).
 *  - options: [{ name, value }] — the selectable items.
 *  - validation, validationMessage: matchPattern rule + its message.
 *  - errorTestId: data-testid on the required-error row.
 *  - data-testid: base id for option test ids (`${id}-option-${value}`).
 *  - ...props: spread onto the Select (e.g. MenuProps, SelectDisplayProps).
 *
 * BEHAVIOR (out): identical to the pre-base FormDropDownReskin.
 *
 * MIGRATION NOTE (shadcn): the frame styling below is the part that changes
 * most — under shadcn it becomes Tailwind classes on <SelectTrigger>, and the
 * MUI-specific workarounds (the !important resets) disappear entirely. The
 * color tokens already come from FIELD.* (colors_v2 CSS vars), so they carry
 * over unchanged.
 */
const BaseDropdown = ({
  errors,
  label,
  required,
  disabled,
  validation,
  validationMessage,
  placeholder = "",
  hidePlaceholder = false,
  options = [],
  control,
  errorTestId,
  ...props
}) => {
  // Track open state so the trigger can drop its bottom corners while the list
  // is open, letting the field and the list read as one joined surface.
  const [open, setOpen] = React.useState(false);

  const hasError =
    errors?.[label]?.type === "required" ||
    errors?.[label]?.type === "matchPattern";

  // Replicate the .onbarding-input frame exactly so the select matches the
  // text fields beside it: 40px tall, --input-background-default fill, 1px
  // --new-border, 6px radius. The inner .MuiSelect-select must be transparent or MUI's
  // standard-variant grey fill shows through.
  const selectSx = {
    // MUI's standard-variant select adds margin-top:16px as a formControl child;
    // clear it so the field aligns flush under its label like the text inputs.
    marginTop: "0 !important",
    boxSizing: "border-box",
    height: FIELD.height,
    // While the list is open the trigger joins the list below it, so flatten
    // its bottom corners; otherwise the full 6px radius.
    borderRadius: open
      ? `${FIELD.radius} ${FIELD.radius} 0 0`
      : FIELD.radius,
    backgroundColor: FIELD.bg,
    border: `1px solid ${FIELD.border}`,
    transition:
      "border-color 0.15s ease, box-shadow 0.15s ease, border-radius 0.15s ease",
    "&.Mui-focused": {
      borderColor: FIELD.borderFocus
    },
    // Hover only paints in the resting state — focus takes priority, so guard
    // hover against the focused state. When the field is in error, hover is
    // suppressed entirely (see the hasError branch below) so the error border
    // always wins.
    ...(!hasError && {
      "&:hover:not(.Mui-disabled):not(.Mui-focused)": {
        borderColor: FIELD.borderHover
      }
    }),
    // Error border wins over both hover and focus: it is applied last and hover
    // is not declared at all in this branch, so a hovered/focused errored field
    // still shows the error border.
    ...(hasError && {
      borderColor: FIELD.errorBorder,
      "&.Mui-focused": {
        borderColor: FIELD.errorBorder
      }
    }),
    // Disabled: muted-border frame on the app-body fill, not-allowed cursor, no
    // hover. Text/placeholder/chevron fall to --new-foreground (see below).
    "&.Mui-disabled": {
      borderColor: FIELD.disabledBorder,
      backgroundColor: FIELD.disabledBg,
      cursor: "not-allowed"
    },
    "& .MuiSelect-select": {
      display: "flex",
      alignItems: "center",
      height: "38px !important",
      minHeight: "38px",
      boxSizing: "border-box",
      padding: "0 40px 0 16px !important",
      // Must be transparent or the standard-variant grey fill shows through.
      backgroundColor: "transparent !important",
      // Match the outer frame: flatten bottom corners while open, animated in
      // lockstep with the frame's border-radius transition.
      borderRadius: open ? `${FIELD.radius} ${FIELD.radius} 0 0` : FIELD.radius,
      transition: "border-radius 0.15s ease",
      fontFamily: "Inter, sans-serif",
      fontSize: 14,
      fontWeight: 400,
      lineHeight: 1.57,
      color: FIELD.text
    },
    "& .MuiSelect-select:focus": {
      backgroundColor: "transparent"
    },
    // Disabled text & placeholder: --new-foreground at half opacity. MUI sets its
    // own .Mui-disabled colour/opacity/cursor on the inner element, so pin all
    // three — the inner .MuiSelect-select covers the whole field, so without the
    // not-allowed cursor here it only shows over the 1px border.
    "&.Mui-disabled .MuiSelect-select": {
      color: `${FIELD.disabledText} !important`,
      WebkitTextFillColor: `${FIELD.disabledText} !important`,
      opacity: "0.5 !important",
      cursor: "not-allowed !important"
    },
    "& .MuiSelect-icon": {
      right: 14,
      // Icon is 16px tall — offset by half its height to vertically centre it.
      top: "calc(50% - 8px)"
    }
    // Disabled chevron colour is set on the icon itself (see IconComponent /
    // DropDownArrowIcon `color`), since .svg-icon's `!important` fill can't be
    // overridden from here.
  };

  // Option rows: --font-strong-default text, accent-01 fill on hover. The
  // selected row is a filled .text-with-icon chip (accent-02 fill, 4px radius,
  // icon-friendly padding) and also drops to accent-01 on hover. MUI's default
  // text/.Mui-selected fills are overridden here.
  const menuItemSx = {
    color: "var(--font-strong-default)",
    "&:hover": { backgroundColor: "var(--app-layer-accent-01)" },
    "&.Mui-selected": {
      height: 38,
      flexGrow: 1,
      display: "flex",
      flexDirection: "row",
      justifyContent: "flex-start",
      alignItems: "center",
      gap: "8px",
      padding: "8px 8px 8px 16px",
      borderRadius: "4px",
      backgroundColor: "var(--app-layer-accent-02)",
      "&:hover": { backgroundColor: "var(--app-layer-accent-01)" }
    }
  };

  // Dropdown list container (the menu Paper): the .Wrapper-Options frame —
  // grows to fit and scrolls past 186px, stretched column, app-body fill,
  // layered drop shadow.
  const menuProps = {
    anchorOrigin: { vertical: "bottom", horizontal: "center" },
    transformOrigin: { vertical: "top", horizontal: "center" },
    ...props.MenuProps,
    PaperProps: {
      ...props.MenuProps?.PaperProps,
      sx: {
        maxHeight: "186px",
        alignSelf: "stretch",
        flexGrow: 0,
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "stretch",
        padding: 0,
        // The list opens flush below the trigger — flatten its top corners so
        // the two read as one joined surface (bottom corners keep 6px radius).
        borderRadius: `0 0 ${FIELD.radius} ${FIELD.radius}`,
        boxShadow: "0 2px 4px -2px var(--10), 0 4px 6px -1px var(--10)",
        backgroundColor: "var(--app-body)",
        // MUI's inner MuiList carries its own 8px vertical padding — zero it so
        // the container has no padding at all.
        "& .MuiList-root": { padding: 0 },
        ...props.MenuProps?.PaperProps?.sx
      }
    }
  };

  const errorContent = (
    <>
      <Collapse in={errors?.[label]?.type === "required"}>
        <span className="text-error" data-testid={errorTestId}>
          <FormattedMessage id={label} /> <FormattedMessage id={"isRequired"} />
        </span>
      </Collapse>
      <Collapse in={errors?.[label]?.type === "matchPattern"}>
        <span className="text-error">{validationMessage}</span>
      </Collapse>
    </>
  );

  // The Select markup is identical whether it is rhf-controlled or not; only the
  // value/onChange/ref wiring differs. `field` is the Controller render arg when
  // a control exists, undefined otherwise (the control-less fallback below).
  const renderSelect = (field) => (
    <Select
      fullWidth
      disableUnderline
      variant="standard"
      defaultValue={""}
      labelId={`select-${label}`}
      id={`simple-select-${label}`}
      name={label}
      disabled={disabled}
      inputRef={field?.ref}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      displayEmpty
      value={field ? field.value : undefined}
      onChange={field?.onChange}
      // Forward rhf's onBlur so the field is marked touched; required for
      // mode:"onTouched" to surface the required error on blur.
      onBlur={field?.onBlur}
      IconComponent={(iconProps) => (
        <DropDownArrowIcon
          className={`material-icons ${iconProps.className}`}
          // Disabled: chevron fill follows --new-foreground (the default
          // .svg-icon rule's !important can't be beaten from sx, so the icon
          // recolors itself via this prop).
          color={disabled ? FIELD.disabledIcon : undefined}
        />
      )}
      sx={selectSx}
      {...props}
      MenuProps={menuProps}
    >
      {/* Empty placeholder row — omitted when hidePlaceholder is set, e.g. for
          fields that always hold a valid value (no empty state). */}
      {!hidePlaceholder && (
        <MenuItem
          value=""
          key={"none"}
          sx={menuItemSx}
          data-testid={`${props["data-testid"] || label}-option-placeholder`}
        >
          <span style={{ color: FIELD.placeholder }}>{placeholder}</span>
        </MenuItem>
      )}
      {options?.map((option, index) => (
        <MenuItem
          value={option.value}
          key={index}
          sx={menuItemSx}
          data-testid={`${props["data-testid"] || label}-option-${option.value}`}
        >
          {option.name}
        </MenuItem>
      ))}
    </Select>
  );

  return (
    <BaseFormField
      label={label}
      required={required}
      errorContent={errorContent}
    >
      {/* Guard against a missing control (e.g. preview/isolated renders): rhf's
          Controller throws on a null control, so fall back to an uncontrolled
          Select rather than crashing. */}
      {control ? (
        <Controller
          name={label}
          control={control}
          rules={{ required: required, validate: { ...validation } }}
          render={({ field }) => renderSelect(field)}
        />
      ) : (
        renderSelect(undefined)
      )}
    </BaseFormField>
  );
};

export default BaseDropdown;
